import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  viewChild
} from '@angular/core';
import { environment } from '../../../../environments/environment';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom, from, switchMap, take, tap, timeout } from 'rxjs';
import { MatDivider } from '@angular/material/divider';
import { MatInput } from '@angular/material/input';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatTab, MatTabChangeEvent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { AuthState } from '../../../core/authentication/auth.state';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { map } from 'rxjs/operators';
import { LearningSectionContainerEntity } from '../learning-section-container-entity';
import { SequenceEntity } from '../../../sequence/sequence.entity';
import { ExplanationContentEntity } from '../../../explanation/content/explanation-content-entity';
import { ExplanationEntity } from '../../../explanation/explanation-entity';
import { TestEntity } from '../../../test/test.entity';
import { LearningSectionContainerEntityService } from '../services/learning-section-container.entity.service';
import {
  ExplanationContentEntityService
} from '../../../explanation/content/services/explanation-content.entity.service';
import { TestEntityService } from '../../../test/services/test.entity.service';
import { SequenceEntityService } from '../../../sequence/services/sequence.entity.service';
import { ExplanationEntityService } from '../../../explanation/services/explanation.entity.service';
import { toggleContenteditable, toggleReadOnlyMathEditorInput } from '../../../core/util';
import { ExplanationContentTabLoader } from '../../../explanation/content/utils/explanation-content-tab.loader';
import { SequenceTabLoader } from '../../../sequence/utils/sequence-tab.loader';
import { ExplanationTabLoader } from '../../../explanation/utils/explanation-tab.loader';
import { TestTabLoader } from '../../../test/utils/test-tab.loader';
import {
  AbstractEntityTabLoader
} from '../../../utils/component/abstract-entity-tab-loader/abstract-entity-tab.loader';
import { messageTypes, WidgetMessageService } from '../../../core/services/widget-message.service';
import { TableLoadingSpinnerComponent } from '../../../utils/component/table-loading-spinner/table-loading-spinner';
import { MatChip, MatChipListbox } from '@angular/material/chips';

@Component({
  selector: 'pixl-form-dialog-learning-section-container-content',
  imports: [
    MatButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogModule,
    AsyncPipe,
    CommonModule,
    MatDivider,
    ReactiveFormsModule,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatError,
    MatInput,
    FormsModule,
    MatIconButton,
    MatCard,
    MatSlideToggle,
    PixlEditorComponent,
    TableLoadingSpinnerComponent,
    MatCardContent,
    MatChipListbox,
    MatChip,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-dialog-learning-section-container-content.component.html',
  styleUrl: './form-dialog-learning-section-container-content.component.scss'
})
export class FormDialogLearningSectionContainerContentComponent implements OnInit, AfterViewInit {
  cdr = inject(ChangeDetectorRef);
  dialogRef = inject(MatDialogRef);
  private widgetService = inject(WidgetMessageService);

  private store = inject(Store);
  private fb = inject(FormBuilder);

  private learningSectionContainerService = inject(LearningSectionContainerEntityService);
  private explanationContentService = inject(ExplanationContentEntityService);
  private testService = inject(TestEntityService);
  private sequenceService = inject(SequenceEntityService);
  private explanationService = inject(ExplanationEntityService);

  public data = inject<LearningSectionContainerEntity>(MAT_DIALOG_DATA);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public missingEntitySelection: any = null;

  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';

  auth$ = this.store.select(AuthState.getToken);
  public authToken: string | null = '';

  public formGroup = this.fb.group({
    title: [this.data.title, Validators.required],
    fullWidth: [this.data.fullWidth],
    content: this.data.content || null
  });

  explanationContentLoader = new ExplanationContentTabLoader(
    this.store,
    this.explanationContentService
  );

  public explanationContents: ExplanationContentEntity[] = [];
  public explanationContentName = new FormControl('', [Validators.required]);
  public explanationContentHtml = '';

  public originalSavedName = '';

  explanationLoader = new ExplanationTabLoader(
    this.store,
    this.explanationService
  );

  public explanations: ExplanationEntity[] = [];
  showExplanationTab = true;

  sequenceLoader = new SequenceTabLoader(
    this.store,
    this.sequenceService
  );

  public sequences: SequenceEntity[] = [];

  testLoader = new TestTabLoader(
    this.store,
    this.testService
  );

  public tests: TestEntity[] = [];

  editor = viewChild(PixlEditorComponent);

  selectedContent: SequenceEntity | ExplanationEntity | ExplanationContentEntity | TestEntity | null = null;
  contentRef?: { id: string, type: string };

  public searchTerm = '';
  public selectedTabLabel: string | null = null;
  public lockTabs = false;
  public activeTabIndex = 0;

  ngOnInit(): void {
    this.loadRelationData();

    this.auth$.pipe(take(1)).subscribe(token => {
      this.authToken = token;
    });

    const query = { include: 'content' };
    type ByIdSelector = (id: string) => LearningSectionContainerEntity | undefined;

    from(this.learningSectionContainerService.ensureSingleLoaded(this.data.id, query))
      .pipe(
        switchMap(() =>
          this.store.select<ByIdSelector>(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            this.learningSectionContainerService.stateClass.byId as any
          ).pipe(
            map(fn => fn(this.data.id)),
            filter((c): c is LearningSectionContainerEntity => !!c),
            take(1)
          )
        ),
        // 2. Setup UI and Wait for Loader Data
        switchMap(async (container) => {
          this.contentRef = (container.content && 'type' in container.content) ?
            container.content :
            undefined;

          // Ensure Tab is set so getActiveLoader() works
          await this.setActiveTab();

          if (!this.contentRef || this.contentRef.type === 'explanation-contents') {
            return container;
          }

          const loader = this.getActiveLoader();

          try {
            await firstValueFrom(
              loader.entities$.pipe(
                filter(list => list && list.length > 0),
                timeout(2500),
                take(1)
              )
            );
          }
          catch {
            console.warn(`Content loader timed out for type: ${this.contentRef.type}`);
          }

          return container;
        }),
        // 3. Finalize selection
        tap(container => {
          if (container && this.contentRef) {
            setTimeout(() => {
              this.selectContentByType(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                this.contentRef!.type as any,
                this.contentRef!.id
              );
              this.cdr.markForCheck();
            }, 0);
          }
        })
      )
      .subscribe();
  }

  async ngAfterViewInit() {
    await this.explanationContentLoader.loadInitial();
    await this.sequenceLoader.loadInitial();
    await this.explanationLoader.loadInitial();
    await this.testLoader.loadInitial();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareContentById(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a || !b) return false;

    // Handle cases where one might be the ID string and the other the object
    const idA = typeof a === 'string' ? a : a.id;
    const idB = typeof b === 'string' ? b : b.id;

    return idA === idB;
  }

  private selectContentByType(
    type: 'explanation-contents' | 'sequences' | 'explanations' | 'tests',
    id: string
  ): void {
    switch (type) {
      case 'explanation-contents':
        // editor content is handled differently
        this.setEditorContent();
        return;

      case 'sequences':
        this.selectFromLoader(this.sequenceLoader, id);
        return;

      case 'explanations':
        this.selectFromLoader(this.explanationLoader, id);
        return;

      case 'tests':
        this.selectFromLoader(this.testLoader, id);
        return;
    }
  }

  private async selectFromLoader<T extends { id: string }>(
    loader: AbstractEntityTabLoader<T>,
    id: string
  ): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loaderAny = loader as any;
    const list = await firstValueFrom(loader.entities$.pipe(take(1)));
    let match = list?.find((e: T) => e.id === id) ?? null;

    if (!match) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let service: any;
      if (loaderAny === this.sequenceLoader) service = this.sequenceService;
      else if (loaderAny === this.testLoader) service = this.testService;
      else if (loaderAny === this.explanationLoader) service = this.explanationService;

      if (service) {
        await service.ensureSingleLoaded(id);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = this.store.selectSnapshot(service.stateClass) as any;
        match = state.entities[id] ?? null;

        if (match && !list.some(e => e.id === id)) {
          this.missingEntitySelection = match;
        }
      }
    }
    else {
      this.missingEntitySelection = null;
    }

    if (match && (!this.originalSavedName || this.originalSavedName === '')) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.originalSavedName = (match as any).name || '';
    }
    // ---------------------

    this.selectedContent = match;
    this.cdr.markForCheck();
  }

  public loadRelationData(): void {
    this.explanationContentLoader.entities$.subscribe(list => {
      this.explanationContents = list;
      this.cdr.markForCheck();
    });

    this.sequenceLoader.entities$.subscribe(list => {
      this.sequences = list;
      this.cdr.markForCheck();
    });

    this.explanationLoader.entities$.subscribe(list => {
      this.explanations = list;
      this.cdr.markForCheck();
    });

    this.testLoader.entities$.subscribe(list => {
      this.tests = list;
      this.cdr.markForCheck();
    });
  }

  /**
   * Maps technical type strings to German labels
   */
  public getContentTypeLabel(type: string | undefined): string {
    const mapping: Record<string, string> = {
      'explanation-contents': 'Editor',
      'sequences': 'Sequenz',
      'explanations': 'Erklärung',
      'tests': 'Test'
    };
    return type ? mapping[type] || type : 'Unbekannt';
  }

  private getTypeForTab(tabLabel: string | null): string | null {
    const mapping: Record<string, string> = {
      editor: 'explanation-contents',
      sequence: 'sequences',
      explanation: 'explanations',
      test: 'tests',
    };

    return tabLabel ? mapping[tabLabel] ?? null : null;
  }

  private async setActiveTab(): Promise<void> {
    const type = this.contentRef?.type;
    const labels = this.getRenderedTabLabels(); // Get current active labels

    let targetLabel = 'editor';
    if (type === 'sequences') targetLabel = 'sequence';
    if (type === 'explanations') targetLabel = 'explanation';
    if (type === 'tests') targetLabel = 'test';

    this.selectedTabLabel = targetLabel;
    // Find the index based on what is actually rendered in the DOM
    const index = labels.indexOf(targetLabel);
    this.activeTabIndex = index >= 0 ? index : 0;
    this.lockTabs = !!this.contentRef;

    const loader = this.getActiveLoader();
    if (loader) {
      await loader.loadInitial();
    }
  }

  async onTabChange(event: MatTabChangeEvent) {
    this.activeTabIndex = event.index;

    const tabLabels = this.getRenderedTabLabels();
    this.selectedTabLabel = tabLabels[event.index] ?? null;

    const loader = this.getActiveLoader();
    if (loader) {
      await loader.loadInitial();
    }
  }

  getRenderedTabLabels(): string[] {
    const labels = ['editor', 'sequence'];
    if (this.showExplanationTab) labels.push('explanation');
    labels.push('test');
    return labels;
  }

  isTabLocked(tabLabel: string): boolean {
    return this.lockTabs && tabLabel !== this.selectedTabLabel;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getActiveLoader(): AbstractEntityTabLoader<any> {
    return [
      this.explanationContentLoader,
      this.sequenceLoader,
      this.explanationLoader,
      this.testLoader,
    ][this.activeTabIndex];
  }

  async onSearchChange() {
    await this.getActiveLoader().search(this.searchTerm);
  }

  async onSelectOpened(
    opened: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loader: AbstractEntityTabLoader<any>,
    select?: MatSelect
  ) {
    if (!opened || !select) return;

    // Wait a tick for the panel to render
    await new Promise(resolve => setTimeout(resolve, 0));

    const panelEl = select.panel?.nativeElement;
    if (!panelEl) return;

    const scrollHandler = async () => {
      const threshold = 50; // px from bottom
      if (panelEl.scrollHeight - panelEl.scrollTop - panelEl.clientHeight < threshold) {
        await loader.loadMore();
      }
    };

    panelEl.addEventListener('scroll', scrollHandler);

    // Cleanup listener when panel closes
    const closeSub = select._closedStream.subscribe(() => {
      panelEl.removeEventListener('scroll', scrollHandler);
      closeSub.unsubscribe();
    });
  }

  async setEditorContent(): Promise<void> {
    if (!this.contentRef?.id) return;

    // 1. Load the full entity
    await this.explanationContentService.ensureSingleLoaded(this.contentRef.id, {});

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const state = this.store.selectSnapshot(this.explanationContentService.stateClass) as any;
    const loadedContent = state.entities[this.contentRef.id];

    if (loadedContent) {
      // 2. Check the loader's list to see if we need the "Shadow Option"
      const list = await firstValueFrom(this.explanationContentLoader.entities$.pipe(take(1)));
      const isInList = list?.some(e => e.id === loadedContent.id);

      if (!isInList) {
        this.missingEntitySelection = loadedContent;
      }
      else {
        this.missingEntitySelection = null;
      }

      // 3. Set the selection and the form value
      this.selectedContent = loadedContent;
      this.explanationContentName.setValue(loadedContent.name || '');

      // 4. Capture original name (frozen for the info card)
      if (!this.originalSavedName) {
        this.originalSavedName = loadedContent.name || '';
      }

      // 5. Handle HTML
      let html = loadedContent.content || '';
      html = toggleReadOnlyMathEditorInput(html, 'remove');
      html = toggleContenteditable(html, 'set');
      this.explanationContentHtml = html;

      // 6. FORCE Change Detection so the mat-select sees the new option
      this.cdr.markForCheck();
    }
  }

  async onEditorSelectClosed(): Promise<void> {
    if (!this.isExplanationContent(this.selectedContent)) return;

    this.explanationContentName.setValue(this.selectedContent.name || '');

    let html = this.selectedContent.content || '';
    html = toggleReadOnlyMathEditorInput(html, 'remove');
    html = toggleContenteditable(html, 'set');

    this.explanationContentHtml = html;
  }

  private isExplanationContent(
    value: SequenceEntity | ExplanationEntity | ExplanationContentEntity | TestEntity | null
  ): value is ExplanationContentEntity {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!value && typeof (value as any).content === 'string';
  }

  checkNewEditorContent(): boolean {
    return !this.isExplanationContent(this.selectedContent);
  }

  private finalizeEditorHtml(): string {
    if (!this.explanationContentHtml?.trim()) {
      return '<p></p>';
    }

    let html = toggleReadOnlyMathEditorInput(this.explanationContentHtml, 'set');
    html = toggleContenteditable(html, 'remove');
    return html;
  }

  public async onCopy(): Promise<void> {
    const originalName = this.explanationContentName.value?.trim() ?? 'Inhalt';
    const copyName = `${originalName} (Kopie)`;

    // 1. Prepare the payload for a brand new entity
    // We use the finalized HTML from the current editor state
    const html = this.finalizeEditorHtml();

    const newContentPayload: Partial<ExplanationContentEntity> = {
      name: copyName,
      content: html,
      version: 1
    };

    try {
      // 2. Create the new entity via the Service
      // This sends the POST request to your Laravel JSON:API
      const created = await this.explanationContentService.create(newContentPayload);

      if (created && created.id) {
        // 3. Update the local UI state
        this.selectedContent = created;
        this.explanationContentName.setValue(created.name);

        // Update the HTML in the editor view
        let displayHtml = created.content || '';
        displayHtml = toggleReadOnlyMathEditorInput(displayHtml, 'remove');
        displayHtml = toggleContenteditable(displayHtml, 'set');
        this.explanationContentHtml = displayHtml;

        this.showMessage('Kopie erstellt.');
        this.cdr.markForCheck();
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (error: any) {
      // Handle the "Unique Name" error even on copy!
      const errors = error?.error?.errors || error?.errors;
      if (Array.isArray(errors) && errors.some(e => e.detail?.includes('unique'))) {
        this.explanationContentName.setValue(copyName);
        this.explanationContentName.setErrors({ unique: true });
        this.explanationContentName.markAsTouched();
        this.showMessage('Kopie existiert bereits.', 'mtError');
      }
      else {
        console.error('Copy failed', error);
        this.showMessage('Fehler beim Erstellen der Kopie.', 'mtError');
      }
    }
  }

  async onSave(): Promise<void> {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formData = this.formGroup.value;
    let contentRelation: { id: string, type: string } | undefined = undefined;
    const type = this.getTypeForTab(this.selectedTabLabel);

    if (this.selectedTabLabel === 'editor') {
      const content = await this.resolveEditorContent();
      if (!content) return;
      contentRelation = { id: content.id, type: 'explanation-contents' };
    }
    else if (this.selectedContent && type) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      contentRelation = { id: (this.selectedContent as any).id, type: type };
    }

    const updatedContainer = Object.assign(
      new LearningSectionContainerEntity(),
      this.data,
      {
        ...formData,
        content: contentRelation,
      }
    );

    try {
      await firstValueFrom(this.learningSectionContainerService.update(updatedContainer));
      await this.learningSectionContainerService.ensureSingleLoaded(this.data.id, { include: 'content' });

      this.showMessage('Container-Inhalt erfolgreich gespeichert.');
      this.dialogRef.close(updatedContainer);
    }
    catch (err) {
      console.error('Error saving container:', err);
      this.showMessage('Fehler beim Speichern des Containers.', 'mtError');
    }
  }

  private showMessage(message: string, messageTye: messageTypes = 'mtSuccess'): void {
    this.widgetService.setMessage({
      message,
      messageTye
    });
  }

  public checkForErrors(formGroup: AbstractControl): string {
    if (formGroup.hasError('required')) {
      return 'Eingabe erforderlich';
    }

    return '';
  }

  private async resolveEditorContent(): Promise<ExplanationContentEntity | null> {
    if (this.explanationContentName.invalid) return null;

    const html = this.finalizeEditorHtml();

    try {
      if (this.checkNewEditorContent()) {
        const newContent: Partial<ExplanationContentEntity> = {
          name: this.explanationContentName.value!,
          content: html,
          version: 1,
        };

        const created = await this.explanationContentService.create(newContent);
        this.selectedContent = created;
        return created;
      }

      if (this.isExplanationContent(this.selectedContent)) {
        const payload = Object.assign(
          new ExplanationContentEntity(),
          this.selectedContent,
          {
            name: this.explanationContentName.value!,
            content: html,
            version: this.selectedContent.version ?? 1,
          }
        );

        const updated = await firstValueFrom(
          this.explanationContentService.update(payload)
        );

        this.selectedContent = updated;
        return updated;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    catch (error: any) {
      const errors = error?.error?.errors || error?.errors;
      if (Array.isArray(errors)) {
        const nameUniqueError = errors.find(e =>
          e.source?.pointer?.includes('name') && e.detail?.includes('unique')
        );

        if (nameUniqueError) {
          // Manually set the "unique" error so the mat-error shows in HTML
          this.explanationContentName.setErrors({ unique: true });
          this.explanationContentName.markAsTouched();
          this.showMessage('Dieser Name wird bereits verwendet.', 'mtError');
        }
        else {
          this.showMessage('Validierungsfehler beim Speichern des Inhalts.', 'mtError');
        }
      }
      return null;
    }

    return null;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
